import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Popover, Select } from "antd";
import classnames from "classnames";
import { RootState } from "store/rootReducer";
import styled from "styled-components";
import {
  LINK_TO_AXIS_LABEL,
  MaxBinCriteria,
  getEntityCountLabel,
  getMaxBinCriteriaLabel,
  getMaxBinSumField
} from "utils/groupBy/maxBinCriteria";

import { DataEnum, IGroupBy } from "models";
import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";

import { Tooltip } from "components/base";
import GroupBy from "components/groupBy";
import { useAvailableLandgrids } from "components/map/hooks/useDataSourceAvailability";

// Stable reference: the picker re-filters its list whenever this array changes.
const NUMERIC_FIELD_TYPES = [DataEnum.Number, DataEnum.Integer];

// The keys that open a closed antd Select.
const OPEN_KEYS = ["Enter", " ", "ArrowDown"];

function focusMenuItem(menu: Nullable<HTMLElement>) {
  (
    menu?.querySelector<HTMLElement>(".isSelected") ??
    menu?.querySelector<HTMLElement>("button")
  )?.focus();
}

// Where Link to Axis exists it is the default, so a pick away from it is stored as false.
const countCriteria = (hasLinkToAxis: boolean): MaxBinCriteria => ({
  MaxBinsSortOrder: "WellCount",
  MaxBinsSortField: undefined,
  MaxBinsLinkToAxis: hasLinkToAxis ? false : undefined
});

// Well Count, so a bin that loses the flag (other chart types) still ranks by count.
const LINK_TO_AXIS_CRITERIA: MaxBinCriteria = {
  MaxBinsSortOrder: "WellCount",
  MaxBinsSortField: undefined,
  MaxBinsLinkToAxis: true
};

interface MaxBinCriteriaSelectProps {
  bin: mBinSize;
  entityKind: EntityKind;
  // What Link to Axis resolves to. The option shows only when it is passed.
  linkedAxisLabel?: string;
  onChange: (criteria: MaxBinCriteria) => void;
  disabled?: boolean;
}

function MaxBinCriteriaSelect({
  bin,
  entityKind,
  linkedAxisLabel,
  onChange,
  disabled = false
}: MaxBinCriteriaSelectProps) {
  const [open, setOpen] = useState(false);
  // Canadian orgs rank by Performance fields, everyone else by Production Summary.
  const { canada, us } = useAvailableLandgrids();
  const defaultCategoryName = canada && !us ? "Performance" : "Production Summary";
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Set by a key press, so focus moves into the menu once it opens.
  const focusMenuOnOpen = useRef(false);
  const categoryList = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.categoryList
      : state.groupBy.facilityFields
  );

  // Absent means linked where the option exists. A flag carried to a surface without
  // it is not sent, so it must not show.
  const hasLinkToAxis = !!linkedAxisLabel;
  const isLinked = hasLinkToAxis && bin.MaxBinsLinkToAxis !== false;
  const criteria = { ...bin, MaxBinsLinkToAxis: isLinked };
  const label = getMaxBinCriteriaLabel(criteria, entityKind, categoryList);
  const sumField = getMaxBinSumField(criteria, entityKind, categoryList);
  const isSum =
    !isLinked &&
    (bin.MaxBinsSortOrder === "FieldSum" || bin.MaxBinsSortOrder === "Production");

  const isOpen = open && !disabled;

  const pickerValue = useMemo(
    () => ({ property: sumField?.property ?? "", title: sumField?.title ?? "" }),
    [sumField?.property, sumField?.title]
  );

  // Runs after the popover has rendered the menu.
  useEffect(() => {
    if (open && focusMenuOnOpen.current) focusMenuItem(menuRef.current);
    focusMenuOnOpen.current = false;
  }, [open]);

  const close = () => {
    setOpen(false);
    if (menuRef.current?.contains(document.activeElement)) {
      triggerRef.current?.querySelector("input")?.focus();
    }
  };

  const pick = (next: MaxBinCriteria) => {
    onChange(next);
    close();
  };

  const onTriggerKeyDown = (event: KeyboardEvent) => {
    if (disabled) return;
    if (event.key === "Escape") return setOpen(false);
    if (!OPEN_KEYS.includes(event.key)) return;
    event.preventDefault();
    if (open) return focusMenuItem(menuRef.current);
    focusMenuOnOpen.current = true;
    setOpen(true);
  };

  const onMenuKeyDown = (event: KeyboardEvent) => {
    const menu = menuRef.current;
    // Keys from the nested field picker reach here through the React tree.
    if (!menu?.contains(event.target as Node)) return;
    if (event.key === "Escape") return close();
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const items = Array.from(menu.querySelectorAll<HTMLElement>("button"));
    const step = event.key === "ArrowDown" ? 1 : -1;
    const current = items.indexOf(document.activeElement as HTMLElement);
    items[(current + step + items.length) % items.length]?.focus();
  };

  const menu = (
    <Menu
      ref={menuRef}
      $width={linkedAxisLabel ? 170 : 150}
      data-testid="maxBinCriteriaMenu"
      onKeyDown={onMenuKeyDown}>
      <MenuItem
        type="button"
        className={classnames({ isSelected: !isLinked && !isSum })}
        data-testid="maxBinCriteriaCount"
        onClick={() => pick(countCriteria(hasLinkToAxis))}>
        <ItemTitle>{getEntityCountLabel(entityKind)}</ItemTitle>
      </MenuItem>
      <GroupBy
        value={pickerValue}
        dataTypeFilters={NUMERIC_FIELD_TYPES}
        entityKind={entityKind}
        placement="right"
        defaultCategoryName={defaultCategoryName}
        hideDateRange
        hideSourceControls
        onChange={(field: IGroupBy) =>
          pick({
            MaxBinsSortOrder: "FieldSum",
            MaxBinsSortField: field.property,
            MaxBinsLinkToAxis: hasLinkToAxis ? false : undefined
          })
        }>
        <MenuItem
          type="button"
          className={classnames({ isSelected: isSum })}
          data-testid="maxBinCriteriaSumOf">
          <ItemTitle>
            Sum of…
            <KeyboardArrowRightIcon fontSize="small" />
          </ItemTitle>
          <ItemSubtitle>{sumField?.title ?? "Select numerical field"}</ItemSubtitle>
        </MenuItem>
      </GroupBy>
      {linkedAxisLabel && (
        <MenuItem
          type="button"
          className={classnames({ isSelected: isLinked })}
          data-testid="maxBinCriteriaLinkToAxis"
          onClick={() => pick(LINK_TO_AXIS_CRITERIA)}>
          <ItemTitle>{LINK_TO_AXIS_LABEL}</ItemTitle>
          <ItemSubtitle>{linkedAxisLabel}</ItemSubtitle>
        </MenuItem>
      )}
    </Menu>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      showArrow={false}
      overlayClassName="popover-no-padding"
      overlayInnerStyle={{ borderRadius: 4 }}
      open={isOpen}
      onOpenChange={(next) => setOpen(next && !disabled)}
      content={menu}>
      <Tooltip title={label}>
        <div
          ref={triggerRef}
          data-testid="maxBinCriteriaSelect"
          onKeyDown={onTriggerKeyDown}>
          {/* Never opens: it only draws the box. Its aria state follows the popover. */}
          <Trigger
            className={classnames({ isOpen })}
            open={false}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            value={label}
            // An empty title stops antd adding a native one under the tooltip.
            options={[{ value: label, label, title: "" }]}
          />
        </div>
      </Tooltip>
    </Popover>
  );
}

export default MaxBinCriteriaSelect;

const Trigger = styled(Select)`
  width: 100%;

  &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 30px;
    padding: 0 4px 0 8px;
    border-color: var(--vape);
    border-radius: var(--border-radius);
    .ant-select-selection-item {
      line-height: 28px;
    }
  }

  &.isOpen .ant-select-selector {
    border-color: var(--color-primary);
  }

  .ant-select-arrow {
    right: 4px;
    color: var(--flint);
  }
`;

const Menu = styled.div<{ $width: number }>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width}px;
  padding: 4px 0;
`;

const ItemTitle = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  font-size: 1.3rem;
  font-weight: var(--fontWeightRegular);
`;

const ItemSubtitle = styled.span`
  font-size: 1.1rem;
  color: var(--lead);
`;

const MenuItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1px;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  background: none;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--color-text-06);
  }

  &.isSelected {
    background-color: var(--color-primary);
    color: var(--color-white);

    ${ItemTitle} {
      font-weight: var(--fontWeightBold);
    }

    ${ItemSubtitle} {
      color: var(--color-white);
    }
  }
`;
